import { Component, OnInit, OnDestroy, inject, DestroyRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormGroup, FormControl, Validators } from '@angular/forms';
import { CardModule } from 'primeng/card';
import { InputTextModule } from 'primeng/inputtext';
import { Select } from 'primeng/select';
import { ButtonModule } from 'primeng/button';
import { DatePicker } from 'primeng/datepicker';
import { TooltipModule } from 'primeng/tooltip';
import { Toast } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { debounceTime, distinctUntilChanged, filter, map, Subject, takeUntil } from 'rxjs';
import { FilingFormStateService } from '../../../services/mbrs/filing-form-state.service';
import { SubmitForValidationComponent } from '../../common/submit-for-validation/submit-for-validation.component';
import { RichTextFieldComponent } from '../../common/rich-text-field/rich-text-field.component';
import { FieldWarningService } from '../../../services/mbrs/field-warning.service';
import { EnvironmentService } from '../../../services/common/environment.service';
import { WarningResult, WarningRule } from '../../../models/validation-warning.model';
import { handleDateInputChange, getFieldMaxLength } from '../../../utils/helpers';
import { FORM_AUTOSAVE_DEBOUNCE_MS } from '../../../utils/debounce-times.util';
import { WarningMessageComponent, WarningMessage } from "../../common/warning-message/warning-message.component";
import { SHEET_KEYS } from '../../../models/sheet-keys';
import { buildFormEditPipeline } from '../../../utils/form-edit-pipeline.util';
import { finalizeSheetInit } from '../../../utils/sheet-init-finalize.util';
import { adoptIntoAutoSaveSheet } from '../../../utils/adopt-remote-sheet-state.util';
import { coerceSchemaDateStrings } from '../../../utils/date-string-coercion.util';
import {
  TOAST_DURATION_DEFAULT_MS,
  TOAST_DURATION_LONG_MS,
} from '../../../utils/toast-durations.util';
import { evaluateConditional } from '../../../services/mbrs/conditional/conditional-field.evaluator';
import { ConditionalRule } from '../../../services/mbrs/conditional/conditional-field.model';
import { PlaceholderPipe, FixedPlaceholderPipe } from '../../../utils/pipes/placeholder.pipe';
import { SelectClearIconBehaviorDirective } from '../../../directives/select-clear-icon-behavior.directive';
import { RequiredMarkerComponent } from '../../common/required-marker/required-marker.component';
import { isControlMandatory } from '../../../utils/required-field.util';

import { SheetLoaderComponent } from '../../common/sheet-loader/sheet-loader.component';
import { computedControlPredicate, revealValidationErrors } from '../../../utils/validation-reveal.util';
import {
  LoadTrace,
  NO_OP_LOAD_TRACE,
  SheetLoadTraceService,
} from '../../../services/mbrs/sheet-load-trace.service';
import { SHEET_LOAD_STEPS } from '../../../utils/sheet-load-trace.util';
import {
  classifySheetInitError,
  fetchJsonAsset,
  fetchSheetSchema,
} from '../../../utils/schema-asset.util';
interface FormSchema {
  role: string;
  definition: string;
  sheetId: string;
  label: string;
  controls: FormControlSchema[];
}

interface FormControlSchema {
  qname: string;
  label: string;
  type: string;
  maxLength?: number;
  ui: {
    control: string;
    placeholder?: string;
    enumSource?: string;
    readonly?: boolean;
    defaultValue?: string;
    inheritFrom?: string;
    showForSubmissionType?: string[];
    hideForSubmissionType?: string[];
    rows?: number;
  };
  required?: boolean;
  requiredIf?: { qname: string; equals: string };
  validators?: Array<{ name: string; patternSource?: string }>;
  triggers?: Array<{
    on: string;
    ifEquals: string;
    then: Array<{ action: string; targets: string[] }>;
  }>;
  warnings?: WarningRule[];
  conditional?: ConditionalRule;
}

@Component({
  selector: 'app-role3-110000-stock-exchange',
  standalone: true,
  imports: [
    SheetLoaderComponent,
    RequiredMarkerComponent,
    CommonModule,
    ReactiveFormsModule,
    CardModule,
    InputTextModule,
    Select,
    SelectClearIconBehaviorDirective,
    ButtonModule,
    DatePicker,
    TooltipModule,
    Toast,
    SubmitForValidationComponent,
    RichTextFieldComponent,
    WarningMessageComponent,
    PlaceholderPipe,
    FixedPlaceholderPipe
  ],
  providers: [MessageService],
  templateUrl: './role3-110000-stock-exchange.component.html',
  styleUrls: ['./role3-110000-stock-exchange.component.css']
})
export class Role3110000StockExchangeComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  private destroyRef = inject(DestroyRef);
  private stateService = inject(FilingFormStateService);
  private messageService = inject(MessageService);
  private loadTrace = inject(SheetLoadTraceService);
  /**
   * This mount's load trace. Opened as the first act of `ngOnInit` and closed
   * either by the terminal step mark or by the init catch, so a failure reports
   * which of the seven load steps this sheet actually reached.
   *
   * @see src/app/utils/sheet-load-trace.util.ts
   */
  private loadTraceHandle: LoadTrace = NO_OP_LOAD_TRACE;
  private warningService = inject(FieldWarningService);
  private envService = inject(EnvironmentService);

  /** Check if the current environment is development (for showing debug info like role IDs) */
  get isDev(): boolean {
    return this.envService.isDevelopment();
  }

  stockExchangeForm!: FormGroup;
  schema!: FormSchema;
  typeDict: any = {};
  isLoading = true;

  // Dropdown options
  dropdownOptions: { [key: string]: any[] } = {};

  // Flag to track if we're loading saved state (to prevent auto-save during load)
  private isLoadingState = false;

  /**
   * Set once this sheet's initial load has settled. Past that point
   * `setupStateListener` stops re-patching the form — see its docblock for why
   * a post-init repaint blanks a date the user is half-way through typing.
   * Mirrors role23/role24, which carry the identical listener and guard.
   */
  private isInitialLoadComplete = false;

  // Dynamic form title with Type of Submission
  /**
   * Card header carrying the filing's submission type, e.g.
   * "Stock Exchange Disclosure (FS-MPERS)".
   *
   * Two rules, both of which this sheet used to break:
   *  - the name comes from the canonical `SHEET_DISPLAY_NAMES` map (via
   *    `getSheetTitleWithSubmissionType`), never a literal repeated here;
   *  - it is a LIVE getter, never an `ngOnInit` snapshot — see the cold-mount
   *    rule on `FilingFormStateService.getFormTitleWithSubmissionType()`.
   */
  get formTitle(): string {
    return this.stateService.getSheetTitleWithSubmissionType(
      SHEET_KEYS.stockExchangeDisclosure
    );
  }

  // Field warnings storage
  fieldWarnings: { [fieldName: string]: WarningResult[] } = {};

  async ngOnInit(): Promise<void> {
    // Open this mount's load trace before anything else, so a failure
    // anywhere below reports which load steps were reached.
    this.loadTraceHandle = this.loadTrace.beginSheetLoad(
      '3',
      this.stateService.getConversionId(),
      this.destroyRef,
    );
    try {
      await this.loadSchema();
      await this.loadTypeDict();
      this.buildForm();
      this.loadTraceHandle?.mark(SHEET_LOAD_STEPS.formBuilt);
      this.loadSavedState();
      this.loadTraceHandle?.mark(SHEET_LOAD_STEPS.stateRestored);
      this.setupStatePersistence();
      this.setupStateListener();
      this.setupWarningEvaluation(); // Setup warning evaluation for all fields

      // Deterministic finalization (replaces the legacy chained
      // setTimeout(100 → 600) that used to live in loadSavedState).
      // Extracted to finalizeInit() so the non-init caller
      // (onCancelConfirmed) can re-finalize after its own
      // loadSavedState() call — see finalizeInit's doc comment.
      await this.finalizeInit();

      this.isLoading = false;
      this.loadTraceHandle?.mark(SHEET_LOAD_STEPS.finalized);
    } catch (error) {
      // Report through the trace instead of a fixed sentence. The toast now
      // names this sheet, the steps it reached, and whether the schema was
      // missing, unreadable, or fine but the form could not be built —
      // `classifySheetInitError` infers the last of those by elimination.
      // Must run BEFORE the loading flag is cleared below: `fail()` closes the
      // trace, which is what stops the finalize mark on the next line from
      // recording this failed init as a success.
      this.loadTraceHandle.fail(classifySheetInitError(error), error);
      this.isLoading = false;
      this.loadTraceHandle?.mark(SHEET_LOAD_STEPS.finalized);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Deterministic sheet-init finalization. Must run after EVERY loadSavedState()
   * (init AND cancel/revert) — the legacy setTimeout lived inside loadSavedState
   * and re-finalized on each call. Safe: all init-time patches use emitEvent:false.
   */
  private async finalizeInit(): Promise<void> {
    await finalizeSheetInit({
      trace: this.loadTraceHandle,
      stateService: this.stateService,
      sheetKey: SHEET_KEYS.stockExchangeDisclosure,
      form: this.stockExchangeForm,
      setLoadingState: (isLoading) => { this.isLoadingState = isLoading; },
      isComputed: computedControlPredicate(this.schema?.controls),
      persistInitial: () => {
        // Repo-wide default: prefer getRawValue() over .value (docs/troubleshooting/
        // troubleshooting.md § "Form data" → "Form values silently drop disabled
        // controls"; also Common Pitfall #2 in root CLAUDE.md) — .value silently
        // drops disabled controls. Paired invariant: persistInitial MUST use the
        // same accessor as this sheet's own auto-save persist (below, in the
        // FORM_AUTOSAVE_DEBOUNCE_MS block) so the re-baselined snapshot equals what
        // auto-save actually writes. Both rules agree here: auto-save also uses
        // getRawValue().
        this.stateService.updateSheetData(
          SHEET_KEYS.stockExchangeDisclosure,
          this.stockExchangeForm.getRawValue(),
          this.stockExchangeForm.valid,
          true, // skipEditedMark
        );
      },
    });

    // Initial load has settled — from here the state listener stops re-patching.
    this.isInitialLoadComplete = true;
  }

  /**
   * Setup listener for external state changes (e.g., data loaded from backend)
   * This allows the form to react when the container populates data via the state service
   */
  private setupStateListener(): void {
    this.stateService.state$
      .pipe(
        map((state) => state.stockExchangeDisclosure),
        distinctUntilChanged(
          (prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)
        ),
        takeUntil(this.destroy$)
      )
      .subscribe((stockExchangeData) => {
        if (stockExchangeData && Object.keys(stockExchangeData).length > 0) {
          // After initial load completes, skip re-patching. `state$` is the
          // MODIFIED stream, so this sheet's own debounced persist comes
          // straight back here — and `patchValue` reaches PrimeNG's
          // `writeValue`, which re-renders the input from the model even under
          // `emitEvent: false`. While a date is half-typed that model is null
          // (`DatePicker.onUserInput` calls `updateModel(null)` on text its
          // `parseValueFromString` rejects), so the echo blanks the digits the
          // user just entered. Adopting a SIBLING TAB's values is
          // `adoptRemoteState()`'s job, on `remoteUpdateApplied$`.
          // Mirrors role12/role23/role24. See isInitialLoadComplete.
          if (this.isInitialLoadComplete) {
            return;
          }

          // Debug Log (Only enable when required to debug, do not allow this to land into prod)
          // console.log('📥 Backend data received for stockExchangeDisclosure:', stockExchangeData);

          // Temporarily set isLoadingState to prevent auto-save during external data load
          this.isLoadingState = true;

          // Convert date strings to Date objects for PrimeNG DatePicker.
          // Schema-driven, and it must NOT be a bare `new Date(...)`: these
          // arrive as the API's "dd/MM/yyyy", which that constructor reads as
          // MM/dd/yyyy — silently transposing any date whose day is =< 12 and
          // yielding an Invalid Date (a blank picker) for every other one.
          // See `date-string-coercion.util.ts`.
          const processedData = this.convertDateStringsToDateObjects(stockExchangeData);

          // Patch the form with the new data
          this.stockExchangeForm.patchValue(processedData, { emitEvent: false });

          // Debug Log (Only enable when required to debug, do not allow this to land into prod)
          // console.log('✓ Form patched with backend data');

          // Re-enable auto-save after a short delay
          setTimeout(() => {
            this.isLoadingState = false;
          }, 100);
        }
      });
  }

  /**
   * Load the form schema from JSON
   */
  private async loadSchema(): Promise<void> {
    this.loadTraceHandle.mark(SHEET_LOAD_STEPS.schemaSelected);
    this.schema = await fetchSheetSchema<FormSchema>(
      this.loadTraceHandle,
      '/assets/forms/role-3.form.json',
    );
    this.loadTraceHandle.mark(SHEET_LOAD_STEPS.schemaParsed);
    // Debug Log (Only enable when required to debug, do not allow this to land into prod)
    // console.log('Schema loaded:', this.schema);
  }

  /**
   * Load type dictionary for dropdown options
   */
  private async loadTypeDict(): Promise<void> {
    // FATAL by design: without the dictionary the dropdowns have no options and
    // the conditional mappings cannot run, so the sheet must not open as if it had
    // loaded. The typed error propagates to the sheet's single catch, which
    // reports ASSET / PARSE / NET through the load trace.
    this.typeDict = await fetchJsonAsset<any>('/assets/types.dict.json');
    this.buildDropdownOptions();
  }

  /**
   * Build dropdown options from type dictionary
   */
  private buildDropdownOptions(): void {
    if (!this.typeDict.enums) return;

    Object.keys(this.typeDict.enums).forEach(key => {
      this.dropdownOptions[key] = this.typeDict.enums[key].map((value: string) => ({
        label: value,
        value: value
      }));
    });
  }

  /**
   * Build the reactive form from schema
   */
  private buildForm(): void {
    if (!this.schema || !this.schema.controls) {
      console.error('Schema not loaded - cannot build form');
      throw new Error('Schema must be loaded before building form');
    }

    // Debug Log (Only enable when required to debug, do not allow this to land into prod)
    // console.log('Building form with schema:', this.schema);

    const formControls: { [key: string]: FormControl<any> } = {};

    this.schema.controls.forEach((control: FormControlSchema) => {
      const validators = [];

      // Add required validator
      if (control.required) {
        validators.push(Validators.required);
      }

      // Get default value
      let defaultValue = '';
      if (control.ui.defaultValue) {
        defaultValue = control.ui.defaultValue;
      }

      // Create form control
      const formControl = new FormControl(
        { value: defaultValue, disabled: control.ui.readonly || false },
        validators
      );

      formControls[control.qname] = formControl;
    });

    this.stockExchangeForm = new FormGroup(formControls);

    // Debug Log (Only enable when required to debug, do not allow this to land into prod)
    // console.log('Form built with controls:', Object.keys(formControls));
  }

  /**
   * Load saved state from state service
   */
  private loadSavedState(): void {
    this.isLoadingState = true;
    this.stateService.markSheetInitializing(SHEET_KEYS.stockExchangeDisclosure);
    const savedData = this.stateService.getSheetData(SHEET_KEYS.stockExchangeDisclosure);

    if (savedData && Object.keys(savedData).length > 0) {
      // Debug Log (Only enable when required to debug, do not allow this to land into prod)
      // console.log('Loading saved state:', savedData);

      // Convert date strings back to Date objects for PrimeNG DatePicker —
      // schema-driven, never a bare `new Date(...)`. See the sibling comment
      // in `setupStateListener()` for what that constructor does to a
      // "dd/MM/yyyy" value.
      const processedData = this.convertDateStringsToDateObjects(savedData);

      this.stockExchangeForm.patchValue(processedData, { emitEvent: false });
    }

    // NOTE: the initial-persist + isLoadingState flip + baseline snapshot
    // (markSheetInitializedWithStatus) are now owned by finalizeSheetInit(),
    // called deterministically from ngOnInit (and from onCancelConfirmed)
    // via finalizeInit() after waitForMbrsHydration(). We keep ONLY the
    // synchronous setup above (markSheetInitializing + the emitEvent:false
    // date-converted patch) here.
  }

  /**
   * Setup auto-save on form changes
   */
  private setupStatePersistence(): void {
    buildFormEditPipeline({
      source$: this.stockExchangeForm.valueChanges,
      debounceMs: FORM_AUTOSAVE_DEBOUNCE_MS,
      destroyRef: this.destroyRef,
      shouldSkip: () => this.isLoadingState,
      persist: () => {
        // getRawValue(), not .value: repo-wide default (docs/troubleshooting/
        // troubleshooting.md § "Form data" → "Form values silently drop disabled
        // controls"; Common Pitfall #2 in root CLAUDE.md) — and it must match
        // finalizeInit()'s persistInitial accessor above so the re-baselined
        // snapshot equals what this auto-save actually writes. Inert today:
        // role-3.form.json declares no readonly/disabledWhen controls and this
        // component makes no imperative .disable() calls, so getRawValue() and
        // .value return identical objects — this is a convention alignment, not
        // a behaviour change.
        const isValid = this.stockExchangeForm.valid;
        this.stateService.updateSheetData(SHEET_KEYS.stockExchangeDisclosure, this.stockExchangeForm.getRawValue(), isValid);
      },
      evaluateStatus: () => {
        this.stateService.updateSheetLevelStatus(SHEET_KEYS.stockExchangeDisclosure, { hasErrors: !this.stockExchangeForm.valid });
      },
    });
  }

  /**
   * Submit form
   */
  submitForm(): void {
    if (this.stockExchangeForm.invalid) {
      this.stockExchangeForm.markAllAsTouched();
      this.messageService.add({
        severity: 'error',
        summary: 'Validation Error',
        detail: 'Please fill in all required fields',
        life: TOAST_DURATION_LONG_MS
      });
      return;
    }

    // Debug Log (Only enable when required to debug, do not allow this to land into prod)
    // Inlined rather than held in a local: the local was unread while the log
    // stayed commented out. getRawValue() per the repo default — see
    // docs/troubleshooting/troubleshooting.md § "Form data".
    // console.log('Form submitted:', this.stockExchangeForm.getRawValue());

    this.messageService.add({
      severity: 'success',
      summary: 'Form Submitted',
      detail: 'Stock exchange disclosure has been saved successfully',
      life: TOAST_DURATION_DEFAULT_MS
    });
  }

  /**
   * Get dropdown options for a field
   */
  getDropdownOptions(enumSource?: string): any[] {
    if (!enumSource) return [];
    const enumKey = enumSource.replace('type:', '');
    return this.dropdownOptions[enumKey] || [];
  }

  /**
   * Check if field should be shown.
   * Schema-driven conditional visibility is evaluated live via the shared
   * evaluateConditional() evaluator — the same logic used by the submit-time
   * projector (role3StockExchangeProjector) — so rendered visibility and
   * submit-strip cannot drift.
   */
  shouldShowField(control: FormControlSchema): boolean {
    const filingInfoData = this.stateService.getActiveFilingInfoData();
    const typeOfSubmission = filingInfoData?.['ssmt-dei:TypeOfSubmission'];

    // Submission-type gates (unchanged).
    if (control.ui.hideForSubmissionType?.includes(typeOfSubmission)) {
      return false;
    }
    if (control.ui.showForSubmissionType && !control.ui.showForSubmissionType.includes(typeOfSubmission)) {
      return false;
    }

    // Schema-driven conditional visibility — single source of truth shared with
    // the submit-time projector (role3StockExchangeProjector) via the same
    // evaluator, so rendered visibility and submit-strip cannot drift.
    if (control.conditional) {
      return evaluateConditional(control.conditional, this.stockExchangeForm.getRawValue()).visible;
    }

    return true;
  }

  /**
   * Check if field is required
   * Note: No fields are required in Role-3 - all validations are warnings only
   */
  isFieldRequired(control: FormControlSchema): boolean {
    // Derived from the schema rather than hard-coded `false`. Role 3 declares no
    // mandatory fields today (all its validations are warnings), so this still
    // returns false for every control — but it now does so BECAUSE the schema
    // says so. The previous hard-coded return meant a future schema edit adding
    // a required field here would render no `*` and fail no test.
    return isControlMandatory(control as any, {
      selfData: this.stockExchangeForm?.getRawValue() ?? {},
    });
  }

  /**
   * Get form control by qname
   */
  getControl(qname: string): any {
    return this.stockExchangeForm.get(qname);
  }

  /**
   * Setup warning evaluation for all fields with warnings defined in schema
   */
  private setupWarningEvaluation(): void {
    if (!this.schema?.controls) return;

    // Find all controls with warnings defined
    const controlsWithWarnings = this.schema.controls.filter(control => control.warnings && control.warnings.length > 0);

    if (controlsWithWarnings.length === 0) return;

    // Evaluate warnings immediately on load (for existing data)
    setTimeout(() => {
      this.evaluateWarningsManually();
    }, 500);

    // Re-evaluate warnings whenever form values change
    this.stockExchangeForm.valueChanges
      .pipe(
        debounceTime(FORM_AUTOSAVE_DEBOUNCE_MS),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.evaluateWarningsManually();
      });

    // ----------------------------------
    // Cross-tab live adoption — when a sibling tab edits THIS sheet's own
    // stockExchangeDisclosure slice, `applyRemoteChange` rehydrates the
    // shared state and publishes `changedSheets` on `remoteUpdateApplied$`.
    // `adoptRemoteState()` below repaints this sheet's form from that state
    // (silently, no emitted valueChanges).
    this.stateService.remoteUpdateApplied$
      .pipe(
        filter(({ streamKey }) => streamKey === 'modifiedState'),
        filter(({ changedSheets }) => changedSheets.includes(SHEET_KEYS.stockExchangeDisclosure)),
        filter(() => !this.stateService.isSheetInitializing(SHEET_KEYS.stockExchangeDisclosure)),
        filter(() => !!this.schema && !!this.stockExchangeForm),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.adoptRemoteState();
      });
    // ----------------------------------
  }

  /**
   * Cross-tab adoption: a sibling tab edited this sheet, `applyRemoteChange` has
   * already rehydrated the shared state, and this repaints the form from it.
   *
   * MUST NOT write back to state — see the invariant on `adoptIntoAutoSaveSheet`.
   * The validation tail below runs for DISPLAY only; the sibling already
   * persisted its own results.
   *
   * Developer Note (TODO): silent adoption is the P.O. decision of 2026-08-17 and
   * is revisitable. If feedback is later wanted, the two options considered were a
   * brief per-row highlight (preferred — it answers "why did that number change?"
   * at the spot it changed) and a per-adoption toast (cheaper, but says nothing
   * about WHAT changed and stacks on repeated sibling edits).
   */
  private adoptRemoteState(): void {
    // Date fields must be coerced BEFORE the patch, exactly as `loadSavedState()`
    // and the sheet's own API-hydration listener already do. A sibling's
    // DISCARD republishes the ORIGINAL slice — which carries the API's raw
    // "dd/MM/yyyy" strings, not the `Date` objects the modified slice holds —
    // so adopting it verbatim wrote a string into `<p-datepicker>`, which
    // renders an unparseable value as an EMPTY field. Reported on role1;
    // role3 shares the seam and owns two date fields of its own. Pinned by
    // `cross-tab-adoption-date-coercion.spec.ts`.
    adoptIntoAutoSaveSheet(
      this.stockExchangeForm,
      this.convertDateStringsToDateObjects(
        this.stateService.getSheetData(SHEET_KEYS.stockExchangeDisclosure)
      )
    );
    this.evaluateWarningsManually();
  }

  /**
   * Convert date strings from persisted/API state back to Date objects.
   *
   * Delegates to the shared `coerceSchemaDateStrings` — see that util's
   * docblock for why an unconverted string renders as a BLANK
   * `<p-datepicker>`, and for which state slice carries which shape. The
   * other filing-info sheets carry the identical one-liner; keep them in step.
   */
  private convertDateStringsToDateObjects(
    data: Record<string, any>
  ): Record<string, any> {
    return coerceSchemaDateStrings(data, this.schema?.controls);
  }


  /**
   * Manually trigger warning evaluation for all fields with warnings
   * This can be called when external data changes (e.g., from other sheets)
   */
  private evaluateWarningsManually(): void {
    if (!this.schema?.controls) return;

    // Find all controls with warnings defined
    const controlsWithWarnings = this.schema.controls.filter(control => control.warnings && control.warnings.length > 0);

    if (controlsWithWarnings.length === 0) return;

    // Get current form values (important for same-form dependencies like conditionalRequired)
    const currentFormValues = this.stockExchangeForm.getRawValue();

    // Structure allValues for both cross-sheet and same-sheet access.
    // Cross-sheet: allValues['filingPrerequisites' | 'filingInfo'][fieldQname]
    //   (buildFilingInfoBagEntry supplies the active slot under BOTH alias
    //   keys so phase-aware resolution stays lossless)
    // Same-sheet: allValues['ssmt:TypeOfExchangeOnWhichCompanyIsListed']
    const allValues = {
      ...this.stateService.buildFilingInfoBagEntry(),
      // Flat structure for same-sheet access (used by conditionalRequired, etc.)
      ...currentFormValues
    };

    controlsWithWarnings.forEach(control => {
      const formControl = this.stockExchangeForm.get(control.qname) as FormControl;
      if (formControl && control.warnings) {
        const warnings = this.warningService.evaluateWarnings(
          formControl,
          control.warnings,
          allValues,
          true // isEditedMode - set to true to enable warning evaluation
        );
        this.fieldWarnings[control.qname] = warnings;
      }
    });

  }

  /**
   * Get warnings for a specific field
   */
  getFieldWarnings(fieldName: string): WarningResult[] {
    return this.fieldWarnings[fieldName] || [];
  }

  /**
   * Check if a field has warnings
   */
  hasWarnings(fieldName: string): boolean {
    return this.getFieldWarnings(fieldName).length > 0;
  }

  /**
   * Split warning message to identify asterisk for styling
   * Returns array of message parts with isAsterisk flag
   */
  splitWarningMessage(message: string): Array<{ text: string; isAsterisk: boolean }> {
    const parts: Array<{ text: string; isAsterisk: boolean }> = [];
    const asteriskPattern = /\*/g;
    let lastIndex = 0;
    let match;

    while ((match = asteriskPattern.exec(message)) !== null) {
      // Add text before asterisk
      if (match.index > lastIndex) {
        parts.push({ text: message.substring(lastIndex, match.index), isAsterisk: false });
      }

      // Add asterisk
      parts.push({ text: '*', isAsterisk: true });
      lastIndex = match.index + 1;
    }

    // Add remaining text after last asterisk
    if (lastIndex < message.length) {
      parts.push({ text: message.substring(lastIndex), isAsterisk: false });
    }

    // If no asterisks found, return the whole message
    if (parts.length === 0) {
      parts.push({ text: message, isAsterisk: false });
    }

    return parts;
  }

  onInputChange(event: InputEvent): void {
    handleDateInputChange(event, this.stockExchangeForm);
  }

  /**
   * Get max length for a field
   * Uses shared helper function from utils/helpers.ts
   */
  getMaxLength(fieldName: string): number | null {
    return getFieldMaxLength(fieldName, this.schema);
  }

  /**
   * Get all warnings for a specific field (for app-warning-message component)
   * Returns array of warning objects with message and severity properties for display
   * Sorted: errors first, then warnings
   */
  getAllWarnings(qname: string): WarningMessage[] {
    const warnings: WarningMessage[] = [];
    const fieldWarnings = this.fieldWarnings[qname] || [];
    fieldWarnings.forEach(warning => {
      // Map severity to 'warning' | 'error' (treat 'info' as 'warning')
      const severity: 'warning' | 'error' = warning.severity === 'error' ? 'error' : 'warning';
      warnings.push({ message: warning.message, severity });
    });

    // Sort: errors first, then warnings
    return warnings.sort((firstWarning, secondWarning) => {
      if (firstWarning.severity === 'error' && secondWarning.severity !== 'error') return -1;
      if (firstWarning.severity !== 'error' && secondWarning.severity === 'error') return 1;
      return 0;
    });
  }

  onCancelConfirmed(): void {
    this.loadSavedState();
    this.loadTraceHandle?.mark(SHEET_LOAD_STEPS.stateRestored);
    this.stockExchangeForm.markAsPristine();
    void this.finalizeInit();
  }
}
